import React, { useCallback, useEffect, useState, useMemo } from "react";
import { api, fmtNum, fmtDate, fmtEUR } from "../lib/api";
import { PageHeader, Surface, Btn, Field, Input, Select, Textarea } from "../components/Primitives";
import { useAuth } from "../contexts/AuthContext";
import { Plus, X, Printer } from "@phosphor-icons/react";

const empty = {
  aircraft_id: "", flight_date: new Date().toISOString().substring(0, 10),
  user_id: "",
  departure: "", arrival: "", hobbs_start: 0, hobbs_end: 0,
  fuel_start: 0, fuel_end: 0, fuel_added: 0, oil_added: 0, squawks: "",
  is_training: false, instructor_hours: 0,
};

export default function Logbook() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin";
  const [logs, setLogs] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");

  const load = useCallback(() => Promise.all([
    api.get(filter ? `/flight-logs?aircraft_id=${filter}` : "/flight-logs").then((r) => setLogs(r.data)),
    api.get("/aircraft").then((r) => setAircraft(r.data)),
    api.get("/memberships").then((r) => setMemberships(r.data)),
    isOwner ? api.get("/participants").then((r) => setParticipants(r.data)) : Promise.resolve(),
  ]), [filter, isOwner]);
  useEffect(() => { load(); }, [load]);

  const loggable = useMemo(() => {
    if (isOwner) return aircraft;
    const ids = new Set(memberships.map((m) => m.aircraft_id));
    return aircraft.filter((a) => ids.has(a.id));
  }, [aircraft, memberships, isOwner]);

  const pilotOptions = useMemo(() => {
    if (!isOwner) return [];
    if (form.aircraft_id) {
      const aMembers = memberships.filter((m) => m.aircraft_id === form.aircraft_id);
      const ids = new Set(aMembers.map((m) => m.user_id));
      const list = aMembers.map((m) => ({ user_id: m.user_id, name: m.user_name }));
      if (user && !ids.has(user.user_id)) list.unshift({ user_id: user.user_id, name: `${user.name} (owner)` });
      return list;
    }
    return participants.map((p) => ({ user_id: p.user_id, name: p.name }));
  }, [isOwner, form.aircraft_id, memberships, participants, user]);

  const save = async () => {
    setErr("");
    try {
      const payload = { ...form, confirmed: true };
      if (!isOwner) delete payload.user_id;
      await api.post("/flight-logs", payload);
      setOpen(false); setForm(empty); load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div>
      <PageHeader overline="Operations" title="Flight log"
        subtitle="Hobbs-based flight records — auto-generates a settlement using that aircraft's cost rates and bank account."
        actions={
          <div className="flex gap-2 print:hidden">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-10 bg-[#0A0A0A] border border-zinc-800 px-3 text-sm font-mono">
              <option value="">All aircraft</option>
              {aircraft.map((a) => <option key={a.id} value={a.id}>{a.registration}</option>)}
            </select>
            <Btn variant="ghost" onClick={() => window.print()} data-testid="print-logbook-btn"><Printer size={14} className="inline mr-1.5" />Print</Btn>
            <Btn data-testid="new-log-btn" onClick={() => setOpen(true)}><Plus size={14} className="inline mr-1.5" />Log flight</Btn>
          </div>
        }
      />
      <div className="p-8">
        <Surface>
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500 font-mono">
              <tr>
                <th className="text-left py-3 px-4">Date</th>
                <th className="text-left py-3 px-4">Pilot</th>
                <th className="text-left py-3 px-4">Aircraft</th>
                <th className="text-left py-3 px-4">DEP → ARR</th>
                <th className="text-right py-3 px-4">Hobbs S</th>
                <th className="text-right py-3 px-4">Hobbs E</th>
                <th className="text-right py-3 px-4">Time</th>
                <th className="text-right py-3 px-4">Fuel €</th>
              </tr>
            </thead>
            <tbody data-testid="logbook-table">
              {logs.length === 0 && <tr><td colSpan={8} className="py-12 text-center text-zinc-500">No flight logs yet.</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-zinc-900 hover:bg-[#1F1F1F]">
                  <td className="py-2.5 px-4 font-mono">{fmtDate(l.flight_date)}</td>
                  <td className="py-2.5 px-4">{l.user_name}</td>
                  <td className="py-2.5 px-4 font-mono text-amber">{l.aircraft_registration}</td>
                  <td className="py-2.5 px-4 font-mono text-zinc-300">{l.departure} → {l.arrival}</td>
                  <td className="py-2.5 px-4 text-right font-mono">{fmtNum(l.hobbs_start, 1)}</td>
                  <td className="py-2.5 px-4 text-right font-mono">{fmtNum(l.hobbs_end, 1)}</td>
                  <td className="py-2.5 px-4 text-right font-mono text-amber">{fmtNum(l.total_time, 2)}</td>
                  <td className="py-2.5 px-4 text-right font-mono">{fmtEUR(l.fuel_cost_eur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" data-testid="log-modal">
          <Surface className="w-full max-w-3xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-medium">Log flight</h3>
              <button onClick={() => setOpen(false)}><X size={20} className="text-zinc-500" /></button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Field label="Aircraft">
                <Select data-testid="log-aircraft" value={form.aircraft_id} onChange={(e) => setForm({ ...form, aircraft_id: e.target.value, user_id: "" })}>
                  <option value="">Select…</option>
                  {loggable.map((a) => <option key={a.id} value={a.id}>{a.registration}</option>)}
                </Select>
              </Field>
              {isOwner ? (
                <Field label="Pilot">
                  <Select data-testid="log-pilot" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
                    <option value="">Select pilot…</option>
                    {pilotOptions.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
                  </Select>
                </Field>
              ) : <div />}
              <Field label="Date"><Input type="date" value={form.flight_date} onChange={(e) => setForm({ ...form, flight_date: e.target.value })} /></Field>
              <Field label="Training">
                <Select value={form.is_training ? "yes" : "no"} onChange={(e) => setForm({ ...form, is_training: e.target.value === "yes" })}>
                  <option value="no">No</option><option value="yes">Yes</option>
                </Select>
              </Field>
              <Field label="Departure (ICAO)"><Input data-testid="log-dep" value={form.departure} onChange={(e) => setForm({ ...form, departure: e.target.value.toUpperCase() })} /></Field>
              <Field label="Arrival (ICAO)"><Input data-testid="log-arr" value={form.arrival} onChange={(e) => setForm({ ...form, arrival: e.target.value.toUpperCase() })} /></Field>
              <Field label="Instructor hours"><Input type="number" step="0.1" value={form.instructor_hours} onChange={(e) => setForm({ ...form, instructor_hours: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Hobbs start"><Input data-testid="log-hobbs-start" type="number" step="0.1" value={form.hobbs_start} onChange={(e) => setForm({ ...form, hobbs_start: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Hobbs end"><Input data-testid="log-hobbs-end" type="number" step="0.1" value={form.hobbs_end} onChange={(e) => setForm({ ...form, hobbs_end: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Total time" hint="Auto-computed">
                <Input value={fmtNum(Math.max(0, form.hobbs_end - form.hobbs_start), 2)} readOnly />
              </Field>
              <Field label="Fuel start (L)"><Input data-testid="log-fuel-start" type="number" step="0.1" value={form.fuel_start} onChange={(e) => setForm({ ...form, fuel_start: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Fuel end (L)"><Input data-testid="log-fuel-end" type="number" step="0.1" value={form.fuel_end} onChange={(e) => setForm({ ...form, fuel_end: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Fuel burn (L)" hint="Auto"><Input value={fmtNum(Math.max(0, form.fuel_start - form.fuel_end), 1)} readOnly /></Field>
              <Field label="Fuel added (L)"><Input type="number" step="0.1" value={form.fuel_added} onChange={(e) => setForm({ ...form, fuel_added: parseFloat(e.target.value || 0) })} /></Field>
              <Field label="Oil added (L)"><Input type="number" step="0.1" value={form.oil_added} onChange={(e) => setForm({ ...form, oil_added: parseFloat(e.target.value || 0) })} /></Field>
              <div className="col-span-3"><Field label="Squawks"><Textarea rows={2} value={form.squawks} onChange={(e) => setForm({ ...form, squawks: e.target.value })} /></Field></div>
            </div>
            {err && <div className="mt-4 text-sm text-red-400 font-mono" data-testid="log-error">{err}</div>}
            <div className="flex justify-end gap-2 mt-6">
              <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
              <Btn onClick={save} data-testid="log-save-btn">Save & generate settlement</Btn>
            </div>
          </Surface>
        </div>
      )}
    </div>
  );
}
